import Vue from 'vue'
import axios from './axios'

const API = process.env.VUE_APP_API

const state = Vue.observable({
  items: [],
  totalAmount: '0.00',
  loading: false,
  toast: null,
  toastTimer: null,
})

function showToast(message, type = 'success') {
  if (state.toastTimer) clearTimeout(state.toastTimer)
  state.toast = { message, type }
  state.toastTimer = setTimeout(() => {
    state.toast = null
  }, 3000)
}

function dismissToast() {
  if (state.toastTimer) clearTimeout(state.toastTimer)
  state.toast = null
}

function mapCartResponse(data) {
  const cart = data.cart || data
  state.items = cart.payload || []
  state.totalAmount = cart.totalAmount || '0.00'
}

async function fetchCart() {
  state.loading = true
  try {
    const res = await axios.get(API + 'cart/contents')
    mapCartResponse(res.data)
  } catch (err) {
    if (err.response && err.response.status === 401) {
      state.items = []
      state.totalAmount = '0.00'
    }
  } finally {
    state.loading = false
  }
}

async function addItem(itemId, count = 1) {
  state.loading = true
  try {
    const res = await axios.post(API + 'cart/add', { id: itemId, count })
    if (res.data.status === 'OK') {
      mapCartResponse(res.data)
      showToast('Item added to cart')
      return { success: true }
    }
    showToast(res.data.message || 'Failed to add item', 'error')
    return { success: false, message: res.data.message }
  } catch (err) {
    const msg = err.response?.data?.message || 'Failed to add item'
    showToast(msg, 'error')
    return { success: false, message: msg, status: err.response?.status }
  } finally {
    state.loading = false
  }
}

async function removeItem(itemId) {
  state.loading = true
  try {
    const res = await axios.post(API + 'cart/remove', { id: itemId })
    if (res.data.status === 'OK') {
      mapCartResponse(res.data)
      showToast('Item removed from cart')
      return { success: true }
    }
    showToast(res.data.message || 'Failed to remove item', 'error')
    return { success: false }
  } catch (err) {
    const msg = err.response?.data?.message || 'Failed to remove item'
    showToast(msg, 'error')
    return { success: false, message: msg }
  } finally {
    state.loading = false
  }
}

async function clearCart() {
  state.loading = true
  try {
    const res = await axios.post(API + 'cart/clear')
    if (res.data.status === 'OK') {
      state.items = []
      state.totalAmount = '0.00'
      showToast('Cart cleared')
      return { success: true }
    }
    return { success: false }
  } catch (err) {
    showToast('Failed to clear cart', 'error')
    return { success: false }
  } finally {
    state.loading = false
  }
}

async function purchaseCart(billingData) {
  state.loading = true
  try {
    const res = await axios.post(API + 'cart/purchase', billingData)
    if (res.data.status === 'OK') {
      if (res.data.redirect_url) {
        return { success: true, redirect: res.data.redirect_url }
      }
      state.items = []
      state.totalAmount = '0.00'
      return { success: true, payload: res.data.payload }
    }
    showToast(res.data.message || 'Purchase failed', 'error')
    return { success: false, message: res.data.message }
  } catch (err) {
    const msg = err.response?.data?.message || 'Purchase failed'
    showToast(msg, 'error')
    return { success: false, message: msg, status: err.response?.status }
  } finally {
    state.loading = false
  }
}

function isInCart(itemId) {
  return state.items.some(item => item.item_id === itemId)
}

function itemCount() {
  return state.items.length
}

function reset() {
  state.items = []
  state.totalAmount = '0.00'
  state.loading = false
  dismissToast()
}

export default {
  state,
  fetchCart,
  addItem,
  removeItem,
  clearCart,
  purchaseCart,
  isInCart,
  itemCount,
  showToast,
  dismissToast,
  reset,
}
